import type { IGetSingleUserLastActiveResItem, IGetUsersLastActiveRes } from "@/lib/backend/users.generated.types";
import type { UserWithRolesMap } from "./userRoles";

export type LastActiveState = "loading" | "error" | number | null;

export const LAST_ACTIVE_BATCH_LIMIT = 200;

export type LastActiveFetcher = (user_uuids: string[]) => Promise<IGetUsersLastActiveRes>;

export const matchesUserFilter = (needle: string, user: UserWithRolesMap): boolean => {
    const normalized = needle.toLowerCase();
    if (normalized.length === 0) {
        return true;
    }
    if (user.title.toLowerCase().indexOf(normalized) > -1) {
        return true;
    }
    if (user.username.toLowerCase().indexOf(normalized) > -1) {
        return true;
    }
    return false;
};

export const getFilteredUsers = (
    users: UserWithRolesMap[],
    title_filter: string,
    only_active: boolean
): UserWithRolesMap[] => {
    let filtered_users = users.filter(user => matchesUserFilter(title_filter, user));
    if (only_active) {
        filtered_users = filtered_users.filter(user => user.active);
    }
    return filtered_users;
};

export const getUuidsToFetch = (
    visible_uuids: string[],
    last_active_by_uuid: Record<string, LastActiveState>
): string[] =>
    visible_uuids.filter(uuid => {
        const cached = last_active_by_uuid[uuid];
        return cached === undefined || cached === "error";
    });

export const createLastActiveBatches = (
    uuids: string[],
    batch_limit: number = LAST_ACTIVE_BATCH_LIMIT
): string[][] => {
    const batches: string[][] = [];
    for (let i = 0; i < uuids.length; i += batch_limit) {
        batches.push(uuids.slice(i, i + batch_limit));
    }
    return batches;
};

export const markUuidsLoading = (
    last_active_by_uuid: Record<string, LastActiveState>,
    uuids: string[]
): Record<string, LastActiveState> => {
    const result = { ...last_active_by_uuid };
    for (const uuid of uuids) {
        result[uuid] = "loading";
    }
    return result;
};

export const mergeLastActiveResponse = (
    last_active_by_uuid: Record<string, LastActiveState>,
    users: IGetSingleUserLastActiveResItem[]
): Record<string, LastActiveState> => {
    const result = { ...last_active_by_uuid };
    for (const item of users) {
        result[item.user_uuid] = item.last_active_at;
    }
    return result;
};

export const markBatchError = (
    last_active_by_uuid: Record<string, LastActiveState>,
    batch: string[]
): Record<string, LastActiveState> => {
    const result = { ...last_active_by_uuid };
    for (const uuid of batch) {
        if (result[uuid] === "loading") {
            result[uuid] = "error";
        }
    }
    return result;
};

export type FetchLastActiveCallbacks = {
    on_loading: (uuids: string[]) => void;
    on_batch_success: (users: IGetSingleUserLastActiveResItem[]) => void;
    on_batch_error: (batch: string[]) => void;
};

export const fetchLastActiveForUsers = async (
    to_fetch: string[],
    fetch_batch: LastActiveFetcher,
    callbacks: FetchLastActiveCallbacks
): Promise<void> => {
    if (to_fetch.length === 0) {
        return;
    }

    callbacks.on_loading(to_fetch);

    for (const batch of createLastActiveBatches(to_fetch)) {
        try {
            const res = await fetch_batch(batch);
            callbacks.on_batch_success(res.users);
        } catch (_err: unknown) {
            callbacks.on_batch_error(batch);
        }
    }
};
